import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { isSearchable, referenceIn } from "@/features/search/lib/query";
import { api, unwrap, type Schemas } from "@/shared/api/client";
import { isApiError } from "@/shared/api/errors";
import { makeKeys } from "@/shared/api/queryKeys";

export type SearchResults = Schemas["SearchResultsOut"];
type Task = Schemas["TaskOut"];

export const searchKeys = makeKeys("search");

const HTTP_NOT_FOUND = 404;

/** The task a reference names, or null when there is none the person can see. */
async function taskByReference(reference: string): Promise<Task | null> {
  try {
    return await unwrap(
      api.GET("/api/v1/tasks/{task_ref}", { params: { path: { task_ref: reference } } }),
    );
  } catch (error) {
    if (isApiError(error) && error.status === HTTP_NOT_FOUND) return null;
    throw error;
  }
}

/**
 * Search across what the person may see. The server matches names and titles; a query shaped
 * like a task reference ("noon-12") also looks that task up directly, so it comes first.
 */
async function search(query: string): Promise<SearchResults> {
  const reference = referenceIn(query);
  const [results, exact] = await Promise.all([
    unwrap(api.GET("/api/v1/search", { params: { query: { q: query } } })),
    reference ? taskByReference(reference) : Promise.resolve(null),
  ]);
  if (!exact) return results;
  return { ...results, tasks: [exact, ...results.tasks.filter((task) => task.id !== exact.id)] };
}

/** Results for an already debounced query; the previous results stay while the next load. */
export function useWorkspaceSearch(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: searchKeys.list({ q: trimmed }),
    queryFn: () => search(trimmed),
    enabled: isSearchable(trimmed),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}
